import { PortfolioService } from '@amzn/awscat-portfolio-assessment-service-client';
import { Portfolio, Tag } from '@amzn/awscat-portfolio-assessment-types';
import { FlashContextInterface, FlashType, withFlashContext } from '@amzn/awscat-react-components';
import { Box, Button, Checkbox, Container, Form, FormField, Header, Input, SpaceBetween, Spinner } from '@amzn/awsui-components-react';
import { useContext, useEffect, useState } from 'react';
import { match, useRouteMatch } from 'react-router';
import { useIsMounted } from 'react-tidy';

import { HelpPanelTopicOptions } from '../../common/Constants';
import { constants } from '../../common/Localization';
import { MatchParam } from '../../common/types';
import { HelpPanelContext } from '../../contexts/HelpPanelContext';
import { PortfolioContext } from '../../contexts/PortfolioContext';
import { RecentPortfoliosContext } from '../../contexts/RecentPortfoliosContext';
import PortfolioTagEditor from './PortfolioTagEditor';

const portfolioService = new PortfolioService();

function PortfolioProperties({ flash }: FlashContextInterface): JSX.Element {
    
    const match: match<MatchParam> | null = useRouteMatch({ 
        path: '/portfolio/:portfolioId'
    }); 
    const portfolioId = match?.params?.portfolioId;

    const [ loading, setLoading ] = useState(true);
    const [ updating, setUpdating ] = useState(false);
    const [ portfolioToUpdate, setPortfolioToUpdate ] = useState<Portfolio | null>(null);

    const { panelHandler } = useContext(HelpPanelContext);
    const { updatePortfolio } = useContext(PortfolioContext);
    const { refreshList } = useContext(RecentPortfoliosContext);

    const isMounted = useIsMounted();

    useEffect(() => {
        panelHandler(HelpPanelTopicOptions.PROPERTIES);
    }, [ panelHandler ]);

    const loadPortfolio = async () => {
        if (portfolioId) {
            setLoading(true);
            try {
                const retrievedPortfolio = await portfolioService.get(portfolioId);
                if (isMounted()) {
                    updatePortfolio(retrievedPortfolio);
                    setPortfolioToUpdate(retrievedPortfolio);
                }
            }
            catch (err) {
                if (isMounted()) {
                    flash.alert(FlashType.error, (err as Error).message, constants.ERROR_LOADING_PORTFOLIO);
                    setPortfolioToUpdate(null);
                }
            } finally {
                if (isMounted()) {
                    setLoading(false);
                }
            }
        }
    };

    useEffect(() => {
        loadPortfolio();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ portfolioId ]);

    const onSubmit = async () => {
        if (!loading && portfolioToUpdate)
        {
            setUpdating(true);
            try {
                await portfolioService.update(portfolioToUpdate);
                updatePortfolio(portfolioToUpdate);
                refreshList();
                flash.alert(FlashType.success, constants.UPDATES_SUCCESSFULLY_SAVED, constants.SUCCESS);
            } catch (err) {
                if (isMounted()) {
                    flash.alert(FlashType.error, (err as Error).message, constants.FAIL_TO_UPDATE_PORTFOLIO);
                }
            }
            finally {
                if (isMounted()) {
                    setUpdating(false);
                }
            }
        }
    };

    const PortfolioSettings = (
        <Container header={<Header variant="h2">{constants.EDIT_PORTFOLIO_PROPERTIES_HEADER}</Header>}>
            {loading ? <Spinner/> : (
                portfolioToUpdate ? (
                    <Box variant='small' margin={{bottom: 's'}}>
                        <FormField
                            description={constants.EDIT_PORTFOLIO_NAME_DESP}
                            label={constants.EDIT_PORTFOLIO_NAME_LABEL}
                        >
                            <Input
                                placeholder={constants.CREATE_PORTFOLIO_NAME_PLACEHOLDER}
                                value={portfolioToUpdate.name}
                                onChange={event => setPortfolioToUpdate({...(portfolioToUpdate as Portfolio), name: event.detail.value})}
                                disabled={updating}
                            />
                        </FormField>
                        <FormField
                            constraintText={constants.TEST_PORTFOLIO_DESCRIPTION} >
                            <Checkbox
                                onChange={event => setPortfolioToUpdate({...(portfolioToUpdate as Portfolio), isTestPortfolio: event.detail.checked})}
                                checked={portfolioToUpdate.isTestPortfolio}
                                disabled={updating}
                            >
                                {constants.EDIT_PORTFOLIO_TEST_LABEL}
                            </Checkbox>
                        </FormField>
                    </Box>
                ) : null 
            )}
        </Container> 
    );

    const PortfolioTags = (
        <PortfolioTagEditor 
            tags={portfolioToUpdate?.tags ?? []}
            setTags={(tags: Tag[]) => setPortfolioToUpdate({...(portfolioToUpdate as Portfolio), tags})}
            loading={loading}
            updating={updating} 
        />
    );

    return (
        <form onSubmit={e => e.preventDefault()}>
            <Form 
                header={
                    <Header variant="h1" description={constants.EDIT_PORTFOLIO_DESP}>
                        {constants.EDIT_PORTFOLIO_HEADER}
                    </Header>
                }
                actions={
                    <SpaceBetween direction="horizontal" size="xs">
                        <Button variant="primary" disabled={loading || updating} loading={updating} 
                            onClick={onSubmit}>{constants.UPDATE_BUTTON_TEXT}
                        </Button>
                    </SpaceBetween>
                }
                errorText={!loading && !portfolioToUpdate ? constants.ERROR_LOADING_PORTFOLIO : ''}
            >
                <SpaceBetween direction="vertical" size="m">
                    {PortfolioSettings}
                    {PortfolioTags}
                </SpaceBetween>
            </Form>
        </form>
    );
};

export default withFlashContext(PortfolioProperties);
